// src/components/orders/EditOrderModal.js
// ============================================================
// MODIFIER UNE COMMANDE — au lieu de l'annuler et de la refaire
// ------------------------------------------------------------
// Un commercial se trompe au guichet. Jusqu'ici il fallait annuler
// la commande et en creer une autre : une annulation restait dans
// les comptes, un coupon mort circulait chez le client, et les
// statistiques de vente etaient faussees.
//
// TROIS PRINCIPES TENUS ICI
// 1. L'ecran DEMANDE AU SERVEUR ce qui est modifiable avant
//    d'afficher quoi que ce soit. Presenter un champ que le serveur
//    refusera, c'est faire ressaisir l'agent pour rien.
// 2. Les consequences sont annoncees AVANT d'enregistrer, pas
//    apres : nouveau total, ecart a rendre ou a encaisser, coupons
//    qui seront reemis.
// 3. Le prix n'est jamais saisi. Il vient de l'offre choisie et est
//    recalcule par le serveur — c'est ce qui empeche d'enregistrer
//    une commande a un prix invente.
//
// Maxula — AbodyStudio Limited
// ============================================================

import React, { useEffect, useState } from 'react';
import {
  FiX, FiLoader, FiAlertTriangle, FiTrash2, FiPlus, FiSearch,
  FiSave, FiArrowLeft, FiInfo
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { orderAPI, dealAPI, merchantAPI } from '../../services/api';

const money = (n) => `${(Number(n) || 0).toFixed(2)} MAD`;

export default function EditOrderModal({ orderId, orderNumber, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [rights, setRights] = useState(null);
  const [order, setOrder] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const [customer, setCustomer] = useState({ name: '', phone: '', email: '', address: '', city: '' });
  // v1.447.0 — commercial crédité de la vente.
  // '' = aucun commercial. On distingue « pas encore chargé » (salesReps vide)
  // de « volontairement aucun » : sans cette distinction, l'ouverture de la
  // fenêtre effacerait l'attribution existante au premier enregistrement.
  const [salesRepId, setSalesRepId] = useState('');
  const [salesReps, setSalesReps] = useState([]);
  const [lines, setLines] = useState([]);
  const [originalTotal, setOriginalTotal] = useState(0);
  // REMISE. Reprise telle quelle a l'ouverture, en montant : c'est la forme
  // enregistree en base. Repasser en pourcentage reste possible a la saisie.
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState('');
  const [originalDiscount, setOriginalDiscount] = useState(0);
  // v1.399.0 — EXTRA (ex. +50 MAD encaisses au guichet), modifiable sans
  // toucher aux lignes.
  const [extraAmount, setExtraAmount] = useState('');
  const [extraLabel, setExtraLabel] = useState('');
  const [originalExtra, setOriginalExtra] = useState({ amount: 0, label: '' });

  // Recherche d'offre (pour remplacer / ajouter une ligne)
  const [picker, setPicker] = useState(false);
  const [mq, setMq] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [merchant, setMerchant] = useState(null);
  const [deals, setDeals] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // v1.443.0 — allSettled et non all.
        //
        // Avec Promise.all, l'echec d'UN des deux appels annulait les deux :
        // la fenetre s'ouvrait entierement vide — ni coordonnees, ni lignes —
        // et le message « les offres ne sont plus modifiables » s'affichait
        // par defaut, ce qui faisait croire a un refus de permission alors
        // que c'etait un appel en erreur.
        //
        // Avec allSettled, chaque moitie vit sa vie : si seuls les droits
        // manquent, on affiche quand meme la commande, et on dit pourquoi.
        const [rEditRes, rOrderRes] = await Promise.allSettled([
          orderAPI.editable(orderId),
          orderAPI.getOne(orderId),
        ]);
        if (!alive) return;

        if (rEditRes.status === 'rejected') {
          const st = rEditRes.reason?.response?.status;
          toast.error(st === 403
            ? "Vous n'avez pas la permission « Modifier une commande »."
            : "Droits de modification indisponibles : la fenêtre est en lecture seule.");
        }
        if (rOrderRes.status === 'rejected') {
          const st = rOrderRes.reason?.response?.status;
          // 403 ici = permission « Voir les commandes » absente sur la fiche.
          throw Object.assign(new Error('order'), {
            response: { data: { message: st === 403
              ? "Vous n'avez pas la permission « Voir les commandes » : impossible d'ouvrir cette fiche."
              : "Impossible d'ouvrir cette commande." } }
          });
        }

        const rEdit = rEditRes.status === 'fulfilled' ? rEditRes.value : null;
        const rOrder = rOrderRes.value;
        const d = rEdit?.data?.data || {};
        setRights(d);
        const o = rOrder?.data?.data?.order || rOrder?.data?.data || null;
        if (o && o.orderNumber) {
          setOrder(o);
          setOriginalTotal(Number(o.total) || 0);
          const rem = Number(o.discountAmount) || 0;
          setOriginalDiscount(rem);
          setDiscountValue(rem > 0 ? String(rem) : '');
          const ex = Number(o.extraAmount) || 0;
          setExtraAmount(ex > 0 ? String(ex) : '');
          setExtraLabel(o.extraLabel || '');
          setOriginalExtra({ amount: ex, label: o.extraLabel || '' });
          setSalesRepId(o.salesRepId ? String(o.salesRepId) : '');
          setCustomer({
            name: o.guestName || (o.customer ? `${o.customer.firstName || ''} ${o.customer.lastName || ''}`.trim() : '') || '',
            phone: o.guestPhone || o.customer?.phone || '',
            email: o.guestEmail || o.customer?.email || '',
            address: o.shippingAddress || '',
            city: o.shippingCity || '',
          });
          setLines((o.items || []).map((it, i) => ({
            key: `l${i}`,
            dealId: it.dealId,
            title: it.title || it.deal?.title || 'Offre',
            price: Number(it.price) || 0,
            quantity: Number(it.quantity) || 1,
          })));
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Impossible d'ouvrir cette commande");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId]);

  // Liste des commerciaux — appel séparé et non bloquant : si la route est
  // indisponible, la fenêtre reste utilisable pour tout le reste.
  useEffect(() => {
    let alive = true;
    orderAPI.staffList()
      .then((r) => { if (alive) setSalesReps(r?.data?.data || []); })
      .catch(() => { if (alive) setSalesReps([]); });
    return () => { alive = false; };
  }, []);

  const searchMerchants = async () => {
    setSearching(true);
    try {
      const r = await merchantAPI.getAll({ search: mq });
      setMerchants(r?.data?.data || r?.data || []);
    } catch (e) { toast.error('Recherche impossible'); }
    finally { setSearching(false); }
  };

  const loadDeals = async (m) => {
    setMerchant(m);
    setSearching(true);
    try {
      const r = await dealAPI.getAll({ merchantId: m.id ?? m._id, limit: 200 });
      setDeals(r?.data?.data || r?.data || []);
    } catch (e) { toast.error('Chargement des offres impossible'); }
    finally { setSearching(false); }
  };

  const addLine = (d) => {
    setLines((prev) => [...prev, {
      key: `n${Date.now()}`,
      dealId: d.id ?? d._id,
      title: d.title,
      // Prix affiche a titre INDICATIF. Le serveur le recalcule a
      // l'enregistrement : ce champ ne sert qu'a montrer l'ordre de grandeur.
      price: Number(d.discountedPrice ?? d.price) || 0,
      quantity: 1,
    }]);
    setPicker(false);
    setMerchant(null);
    setDeals([]);
  };

  const setQty = (key, q) => {
    const n = Math.max(1, Math.min(100, Number(q) || 1));
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, quantity: n } : l)));
  };
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const sousTotal = lines.reduce((s, l) => s + l.price * l.quantity, 0);
  const remise = (() => {
    const v = Number(discountValue);
    if (!Number.isFinite(v) || v <= 0) return 0;
    const r = discountType === 'percent' ? sousTotal * Math.min(100, v) / 100 : v;
    return Math.min(Math.max(0, Math.round(r * 100) / 100), sousTotal);
  })();
  const extraVal = (() => {
    const v = Number(extraAmount);
    return Number.isFinite(v) && v > 0 ? Math.round(v * 100) / 100 : 0;
  })();
  const shipping = Number(order?.shippingFee) || 0;
  // Le total inclut l'extra et la livraison : sans eux, « a rendre au client »
  // s'affichait a tort des qu'une commande avait deja un extra.
  const newTotal = Math.round((sousTotal - remise + extraVal + shipping) * 100) / 100;
  const extraChangee = Math.abs(extraVal - (Number(originalExtra.amount) || 0)) > 0.001
    || (extraVal > 0 && extraLabel.trim() !== String(originalExtra.label || '').trim());
  const delta = newTotal - originalTotal;
  const remiseChangee = Math.abs(remise - originalDiscount) > 0.001;
  const changed = (a) => a; // lisibilite
  const itemsChanged = rights?.items && JSON.stringify(
    (order?.items || []).map((i) => [i.dealId, Number(i.quantity)]).sort()
  ) !== JSON.stringify(lines.map((l) => [l.dealId, Number(l.quantity)]).sort());

  const save = async () => {
    if (itemsChanged && !lines.length) { toast.error('La commande doit garder au moins une ligne'); return; }
    setSaving(true);
    try {
      const body = { customer };
      // Envoyé uniquement si la valeur a changé : transmettre systématiquement
      // ce champ ferait écraser l'attribution par '' quand la liste n'a pas pu
      // se charger.
      if (String(salesRepId || '') !== String(order?.salesRepId || '')) {
        body.salesRepId = salesRepId === '' ? null : salesRepId;
      }
      if (itemsChanged) body.items = lines.map((l) => ({ dealId: l.dealId, quantity: l.quantity }));
      // Seule la REGLE est envoyee, jamais le total : le serveur recalcule.
      if (itemsChanged || remiseChangee) body.discount = { type: discountType, value: Number(discountValue) || 0 };
      if (itemsChanged || extraChangee) body.extra = { amount: extraVal, label: extraLabel.trim() };
      const r = await orderAPI.updateOrder(orderId, body);
      toast.success(r?.data?.message || 'Commande modifiée');
      onSaved && onSaved();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Modification refusée');
    } finally {
      setSaving(false);
      setConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b shrink-0">
          <div>
            <h2 className="text-lg font-bold">Modifier la commande</h2>
            <p className="text-sm text-gray-500 font-mono">{orderNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Fermer">
            <FiX size={22} />
          </button>
        </div>

        {loading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
            <FiLoader className="animate-spin" /> Chargement…
          </div>
        ) : (
          <>
            <div className="p-5 space-y-5 overflow-y-auto">
              {/* CE QUE LE SERVEUR INTERDIT, dit d'emblee. */}
              {rights?.raisons?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                  <FiAlertTriangle className="mt-0.5 shrink-0" size={16} />
                  <div>
                    {rights.raisons.map((r, i) => <div key={i}>{r}</div>)}
                  </div>
                </div>
              )}

              {/* ---- COORDONNEES ---- */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Client</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {[
                    ['name', 'Nom et prénom'],
                    ['phone', 'Téléphone'],
                    ['email', 'Email'],
                    ['city', 'Ville'],
                  ].map(([k, label]) => (
                    <div key={k}>
                      <label className="block text-xs text-gray-500 mb-1">{label}</label>
                      <input
                        value={customer[k]}
                        onChange={(e) => setCustomer((c) => ({ ...c, [k]: e.target.value }))}
                        disabled={!rights?.customer}
                        className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                      />
                    </div>
                  ))}
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">Adresse</label>
                    <input
                      value={customer.address}
                      onChange={(e) => setCustomer((c) => ({ ...c, address: e.target.value }))}
                      disabled={!rights?.customer}
                      className="w-full border rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* ---- COMMERCIAL CREDITE (v1.447.0) ----
                  Bloc distinct du client : ce n'est pas une coordonnee, c'est
                  l'attribution de la vente. Elle alimente « Rendement des
                  commerciaux » et, en pratique, les primes — d'ou l'importance
                  de pouvoir la corriger apres coup.

                  Reste modifiable meme sur une commande payee : reattribuer
                  une vente ne touche ni au total, ni au stock, ni a ce qui est
                  du au partenaire. Il n'est donc pas soumis a rights.items. */}
              <div>
                <h3 className="font-semibold text-sm mb-2">Commercial</h3>
                <select
                  value={salesRepId}
                  onChange={(e) => setSalesRepId(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                >
                  <option value="">Aucun commercial</option>
                  {salesReps.map((r) => (
                    <option key={r.id} value={String(r.id)}>{r.name}</option>
                  ))}
                  {/* Le commercial actuel peut ne plus figurer dans la liste
                      (compte suspendu) : sans cette option de secours, le menu
                      afficherait « Aucun commercial » et le premier
                      enregistrement effacerait l'attribution sans prevenir. */}
                  {salesRepId && !salesReps.some((r) => String(r.id) === String(salesRepId)) && (
                    <option value={String(salesRepId)}>
                      Commercial #{salesRepId} (compte inactif)
                    </option>
                  )}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Vente créditée à ce commercial dans « Rendement des commerciaux ».
                </p>
              </div>

              {/* ---- LIGNES ---- */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-sm">Offres commandées</h3>
                  {rights?.items && !picker && (
                    <button
                      type="button"
                      onClick={() => setPicker(true)}
                      className="text-sm font-semibold text-[#e11d7b] hover:underline flex items-center gap-1"
                    >
                      <FiPlus size={14} /> Ajouter / remplacer une offre
                    </button>
                  )}
                </div>

                {!rights?.items && (
                  <p className="text-xs text-gray-500 mb-2">
                    Les offres ne sont plus modifiables sur cette commande. Vous
                    pouvez toujours corriger les coordonnées du client ci-dessus.
                  </p>
                )}

                <div className="space-y-2">
                  {lines.map((l) => (
                    <div key={l.key} className="flex items-center gap-3 border rounded-lg px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{l.title}</div>
                        <div className="text-xs text-gray-500">{money(l.price)} / unité</div>
                      </div>
                      <input
                        type="number" min="1" max="100"
                        value={l.quantity}
                        onChange={(e) => setQty(l.key, e.target.value)}
                        disabled={!rights?.items}
                        className="w-16 border rounded-lg px-2 py-1 text-sm text-center disabled:bg-gray-100"
                      />
                      <div className="w-24 text-right text-sm font-semibold">{money(l.price * l.quantity)}</div>
                      {rights?.items && lines.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeLine(l.key)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          aria-label="Retirer cette ligne"
                        >
                          <FiTrash2 size={15} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Choix d'une nouvelle offre : marchand puis offre, comme a la
                    creation — une liste globale de 800 offres est inutilisable. */}
                {picker && (
                  <div className="mt-3 border rounded-lg p-3 bg-gray-50">
                    {!merchant ? (
                      <>
                        <div className="flex gap-2 mb-2">
                          <input
                            value={mq}
                            onChange={(e) => setMq(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && searchMerchants()}
                            placeholder="Rechercher un partenaire…"
                            className="flex-1 border rounded-lg px-3 py-2 text-sm"
                          />
                          <button type="button" onClick={searchMerchants}
                            className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm flex items-center gap-1">
                            <FiSearch size={14} /> Chercher
                          </button>
                          <button type="button" onClick={() => setPicker(false)}
                            className="px-3 py-2 rounded-lg border text-sm">Annuler</button>
                        </div>
                        {searching ? (
                          <div className="text-sm text-gray-500 py-2">Recherche…</div>
                        ) : (
                          <div className="max-h-44 overflow-y-auto divide-y">
                            {merchants.map((m) => (
                              <button key={m.id ?? m._id} type="button" onClick={() => loadDeals(m)}
                                className="w-full text-left px-2 py-2 text-sm hover:bg-white">
                                {m.businessName || m.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => { setMerchant(null); setDeals([]); }}
                          className="text-sm text-gray-600 hover:underline flex items-center gap-1 mb-2">
                          <FiArrowLeft size={14} /> {merchant.businessName || merchant.name}
                        </button>
                        {searching ? (
                          <div className="text-sm text-gray-500 py-2">Chargement…</div>
                        ) : (
                          <div className="max-h-52 overflow-y-auto divide-y">
                            {deals.map((d) => (
                              <button key={d.id ?? d._id} type="button" onClick={() => addLine(d)}
                                className="w-full text-left px-2 py-2 hover:bg-white flex justify-between gap-3">
                                <span className="text-sm truncate">{d.title}</span>
                                <span className="text-sm font-semibold whitespace-nowrap">
                                  {money(d.discountedPrice ?? d.price)}
                                </span>
                              </button>
                            ))}
                            {!deals.length && <div className="text-sm text-gray-500 py-2">Aucune offre.</div>}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* ---- REMISE ---- */}
              {rights?.items && (
                <div className="rounded-lg border border-gray-200 p-3">
                  <div className="text-sm font-semibold mb-2">Remise</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                      <button type="button" onClick={() => setDiscountType('amount')}
                        className={`px-3 py-2 text-sm font-semibold ${discountType === 'amount' ? 'bg-[#e11d7b] text-white' : 'bg-white text-gray-700'}`}>MAD</button>
                      <button type="button" onClick={() => setDiscountType('percent')}
                        className={`px-3 py-2 text-sm font-semibold ${discountType === 'percent' ? 'bg-[#e11d7b] text-white' : 'bg-white text-gray-700'}`}>%</button>
                    </div>
                    <input type="number" min="0" step="0.01" value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                      placeholder={discountType === 'percent' ? 'Ex : 10' : 'Ex : 50'}
                      className="w-32 border rounded-lg px-3 py-2 text-sm" />
                    {remise > 0 && (
                      <span className="text-sm text-green-700 font-semibold">
                        −{money(remise)} → {money(newTotal)}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Prise sur la marge Maxula : le partenaire reste payé sur le
                    prix d'origine.
                  </p>
                </div>
              )}

              {/* ---- EXTRA ---- */}
              {rights?.customer && (
                <div className="rounded-lg border border-gray-200 p-3">
                  <div className="text-sm font-semibold mb-2">Extra (optionnel)</div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input type="number" min="0" step="0.01" value={extraAmount}
                      onChange={(e) => setExtraAmount(e.target.value)}
                      placeholder="Ex : 50"
                      className="w-32 border rounded-lg px-3 py-2 text-sm" />
                    <input type="text" value={extraLabel}
                      onChange={(e) => setExtraLabel(e.target.value)}
                      placeholder="Motif (ex : option, supplément…)"
                      className="flex-1 min-w-[180px] border rounded-lg px-3 py-2 text-sm" />
                  </div>
                  {extraVal > 0 && (
                    <p className="text-sm text-amber-700 font-semibold mt-2">
                      + {money(extraVal)} → total {money(newTotal)}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Montant encaissé en plus par Maxula. Il apparaît sur le bon de commande et le reçu.
                    Mettez 0 pour le retirer.
                  </p>
                </div>
              )}

              {/* ---- CONSEQUENCES, avant d'enregistrer ---- */}
              {(itemsChanged || remiseChangee || extraChangee) && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm space-y-1">
                  <div className="flex justify-between"><span>Total avant</span><span>{money(originalTotal)}</span></div>
                  <div className="flex justify-between"><span>Sous-total</span><span>{money(sousTotal)}</span></div>
                  {remise > 0 && (
                    <div className="flex justify-between text-green-700"><span>Remise</span><span>−{money(remise)}</span></div>
                  )}
                  {extraVal > 0 && (
                    <div className="flex justify-between text-amber-700"><span>Extra{extraLabel.trim() ? ` — ${extraLabel.trim()}` : ''}</span><span>+{money(extraVal)}</span></div>
                  )}
                  {shipping > 0 && (
                    <div className="flex justify-between"><span>Livraison</span><span>+{money(shipping)}</span></div>
                  )}
                  <div className="flex justify-between font-semibold">
                    <span>Nouveau total</span><span>{money(newTotal)}</span>
                  </div>
                  {Math.abs(delta) > 0.001 && (
                    <div className={`flex justify-between font-bold ${delta > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                      <span>{delta > 0 ? 'À encaisser en plus' : 'À rendre au client'}</span>
                      <span>{money(Math.abs(delta))}</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 pt-1 flex items-start gap-1.5">
                    <FiInfo className="mt-0.5 shrink-0" size={13} />
                    Le prix définitif est recalculé par le serveur à partir des offres
                    choisies. {rights?.paid ? 'Les coupons actuels seront annulés et de nouveaux seront émis — prévenez le client s\'il est déjà reparti.' : ''}
                  </p>
                </div>
              )}
            </div>

            <div className="border-t p-4 flex justify-end gap-2 shrink-0">
              <button type="button" onClick={onClose}
                className="px-4 py-2 rounded-lg border text-sm font-medium hover:bg-gray-50">
                Annuler
              </button>
              <button
                type="button"
                onClick={() => (itemsChanged || remiseChangee || extraChangee ? setConfirm(true) : save())}
                disabled={saving || (!rights?.customer && !rights?.items)}
                className="px-5 py-2 rounded-lg bg-[#e11d7b] text-white font-semibold hover:opacity-90 disabled:opacity-60 flex items-center gap-2"
              >
                <FiSave size={15} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </>
        )}

        {/* Confirmation UNIQUEMENT quand les lignes changent : c'est le seul cas
            qui touche l'argent, le stock et les coupons. Corriger un nom ne
            merite pas une question de plus. */}
        {confirm && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
            onClick={() => !saving && setConfirm(false)}>
            <div className="bg-white rounded-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center gap-2 mb-3">
                <FiAlertTriangle className="text-amber-500" size={20} />
                <h3 className="font-bold">Confirmer la modification</h3>
              </div>
              <ul className="text-sm text-gray-700 space-y-1.5 mb-4 list-disc pl-5">
                <li>Le total passe de <strong>{money(originalTotal)}</strong> à <strong>{money(newTotal)}</strong>.</li>
                {Math.abs(delta) > 0.001 && (
                  <li className="font-semibold">
                    {delta > 0
                      ? `${money(delta)} restent à encaisser auprès du client.`
                      : `${money(-delta)} sont à rendre au client.`}
                  </li>
                )}
                <li>Le stock des anciennes offres est rendu, celui des nouvelles est pris.</li>
                {rights?.paid && <li>Les coupons actuels sont annulés et de nouveaux sont émis.</li>}
                <li>Les gains du partenaire sont recalculés.</li>
              </ul>
              <div className="flex gap-2">
                <button type="button" onClick={() => setConfirm(false)} disabled={saving}
                  className="flex-1 px-4 py-2 rounded-lg border text-sm font-medium">Revenir</button>
                <button type="button" onClick={save} disabled={saving}
                  className="flex-1 px-4 py-2 rounded-lg bg-[#e11d7b] text-white text-sm font-bold disabled:opacity-60">
                  {saving ? 'Enregistrement…' : 'Confirmer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
